<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

type Axis = 'x' | 'y' | 'z'

interface OrbPoint {
  x: number
  y: number
  z: number
  color: number
}

interface OrbMove {
  axis: Axis
  min: number
  max: number
  direction: 1 | -1
}

interface ProjectedPoint {
  x: number
  y: number
  z: number
  radius: number
  color: number
  active: boolean
}

const props = withDefaults(defineProps<{
  size?: number
}>(), {
  size: 124
})

const canvas = ref<HTMLCanvasElement | null>(null)

const MOVE_DURATION_SECONDS = 0.42
const REST_DURATION_SECONDS = 1.1
const COLOR_SEQUENCE = [0, 1, 0, 1, 2, 0, 3, 1, 0, 4] as const
const COLOR_TOKENS = [
  { token: '--tq-color-teal', fallback: '#00ccb8' },
  { token: '--tq-color-navy', fallback: '#202078' },
  { token: '--tq-color-orange', fallback: '#ff9900' },
  { token: '--tq-color-yellow', fallback: '#fcdc00' },
  { token: '--tq-color-magenta', fallback: '#e93a88' }
] as const
const COLOR_STRENGTHS = [64, 72, 58, 46, 55] as const
const MOVES: OrbMove[] = [
  { axis: 'y', min: 0.34, max: 1.01, direction: 1 },
  { axis: 'x', min: -1.01, max: -0.34, direction: -1 },
  { axis: 'z', min: -0.33, max: 0.33, direction: 1 },
  { axis: 'y', min: -1.01, max: -0.34, direction: -1 },
  { axis: 'x', min: 0.34, max: 1.01, direction: 1 },
  { axis: 'z', min: 0.34, max: 1.01, direction: -1 },
  { axis: 'y', min: -0.33, max: 0.33, direction: 1 },
  { axis: 'x', min: -0.33, max: 0.33, direction: -1 }
]
const POINTS = createSpherePoints()

let animationFrame = 0
let context: CanvasRenderingContext2D | null = null
let deviceScale = 1
let reduceMotion: MediaQueryList | null = null
let colors: string[] = COLOR_TOKENS.map(color => color.fallback)

function createSpherePoints() {
  const points: OrbPoint[] = []
  const latitudeRings = 16
  const longitudeDensity = 42

  for (let latitudeIndex = 0; latitudeIndex <= latitudeRings; latitudeIndex += 1) {
    const latitude = -Math.PI / 2 + (latitudeIndex / latitudeRings) * Math.PI
    const cosLatitude = Math.cos(latitude)
    const longitudeCount = Math.max(1, Math.round(Math.abs(cosLatitude) * longitudeDensity))

    for (let longitudeIndex = 0; longitudeIndex < longitudeCount; longitudeIndex += 1) {
      const longitude = (longitudeIndex / longitudeCount) * Math.PI * 2
      const colorKey = (latitudeIndex * 7 + longitudeIndex * 11) % COLOR_SEQUENCE.length

      points.push({
        x: cosLatitude * Math.cos(longitude),
        y: Math.sin(latitude),
        z: cosLatitude * Math.sin(longitude),
        color: COLOR_SEQUENCE[colorKey] ?? 0
      })
    }
  }

  return points
}

function resolveColors() {
  if (!canvas.value) return

  const styles = getComputedStyle(document.documentElement)
  const isDark = document.documentElement.classList.contains('dark')
  const blendTarget = styles.getPropertyValue(isDark ? '--tq-bg-strong' : '--tq-text-muted').trim()

  colors = COLOR_TOKENS.map(({ token, fallback }, index) => {
    const baseColor = styles.getPropertyValue(token).trim() || fallback
    const strength = COLOR_STRENGTHS[index] ?? 60

    return blendTarget
      ? `color-mix(in oklch, ${baseColor} ${strength}%, ${blendTarget})`
      : fallback
  })
}

function solveState(timeSeconds: number) {
  const movementDuration = MOVES.length * MOVE_DURATION_SECONDS * 2
  const cycleDuration = movementDuration + REST_DURATION_SECONDS
  const cycleTime = timeSeconds % cycleDuration
  const amounts = new Array<number>(MOVES.length).fill(0)
  let activeMove = -1

  if (cycleTime >= movementDuration) return { amounts, activeMove }

  const slot = Math.floor(cycleTime / MOVE_DURATION_SECONDS)
  const slotProgress = (cycleTime - slot * MOVE_DURATION_SECONDS) / MOVE_DURATION_SECONDS
  const easedProgress = 1 - (1 - Math.min(1, slotProgress / 0.72)) ** 3

  if (slot < MOVES.length) {
    for (let index = 0; index < slot; index += 1) amounts[index] = 1
    amounts[slot] = easedProgress
    activeMove = slot
  }
  else {
    amounts.fill(1)
    const reverseSlot = slot - MOVES.length
    const moveIndex = MOVES.length - 1 - reverseSlot

    for (let index = MOVES.length - 1; index > moveIndex; index -= 1) amounts[index] = 0
    amounts[moveIndex] = 1 - easedProgress
    activeMove = moveIndex
  }

  return { amounts, activeMove }
}

function rotateAroundAxis(
  x: number,
  y: number,
  z: number,
  axis: Axis,
  angle: number
): [number, number, number] {
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)

  if (axis === 'x') return [x, y * cosine - z * sine, y * sine + z * cosine]
  if (axis === 'y') return [x * cosine + z * sine, y, -x * sine + z * cosine]

  return [x * cosine - y * sine, x * sine + y * cosine, z]
}

function transformPoint(
  point: OrbPoint,
  amounts: number[],
  activeMove: number,
  timeSeconds: number
): ProjectedPoint {
  let { x, y, z } = point
  let active = false

  MOVES.forEach((move, index) => {
    const amount = amounts[index]
    if (!amount) return

    const coordinate = move.axis === 'x' ? x : move.axis === 'y' ? y : z
    if (coordinate < move.min || coordinate >= move.max) return

    active ||= index === activeMove
    ;[x, y, z] = rotateAroundAxis(x, y, z, move.axis, move.direction * Math.PI / 2 * amount)
  })

  ;[x, y, z] = rotateAroundAxis(x, y, z, 'y', timeSeconds * 0.34)
  ;[x, y, z] = rotateAroundAxis(x, y, z, 'x', 0.34 + Math.sin(timeSeconds * 0.7) * 0.045)

  const depth = (z + 1) / 2
  const sphereRadius = props.size * 0.365
  const dotScale = props.size / 124

  return {
    x: props.size / 2 + x * sphereRadius,
    y: props.size / 2 + y * sphereRadius,
    z,
    radius: (0.72 + depth * 1.42 + (active ? 0.48 : 0)) * dotScale,
    color: active ? (point.color % 2 ? 2 : 0) : point.color,
    active
  }
}

function paintDot(point: ProjectedPoint) {
  if (!context) return

  const depth = (point.z + 1) / 2
  const color = colors[point.color] ?? COLOR_TOKENS[point.color]?.fallback ?? '#00ccb8'

  if (point.active) {
    context.globalAlpha = 0.06 + depth * 0.05
    context.fillStyle = color
    context.beginPath()
    context.arc(point.x, point.y, point.radius * 2.1, 0, Math.PI * 2)
    context.fill()
  }

  context.globalAlpha = 0.22 + depth * 0.64
  context.fillStyle = color
  context.beginPath()
  context.arc(point.x, point.y, point.radius, 0, Math.PI * 2)
  context.fill()
}

function draw(timeSeconds: number) {
  if (!context) return

  context.setTransform(deviceScale, 0, 0, deviceScale, 0, 0)
  context.clearRect(0, 0, props.size, props.size)

  const { amounts, activeMove } = solveState(timeSeconds)
  const projectedPoints = POINTS
    .map(point => transformPoint(point, amounts, activeMove, timeSeconds))
    .sort((left, right) => left.z - right.z)

  projectedPoints.forEach(paintDot)
  context.globalAlpha = 1
}

function loop(timestamp: number) {
  draw(timestamp / 1000)
  animationFrame = requestAnimationFrame(loop)
}

function stopAnimation() {
  cancelAnimationFrame(animationFrame)
}

function startAnimation() {
  stopAnimation()

  if (reduceMotion?.matches) {
    const movementDuration = MOVES.length * MOVE_DURATION_SECONDS * 2
    draw(movementDuration + REST_DURATION_SECONDS / 2)
    return
  }

  animationFrame = requestAnimationFrame(loop)
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    stopAnimation()
    return
  }

  startAnimation()
}

function handleMotionPreferenceChange() {
  startAnimation()
}

onMounted(() => {
  if (!canvas.value) return

  deviceScale = Math.min(2, window.devicePixelRatio || 1)
  canvas.value.width = Math.round(props.size * deviceScale)
  canvas.value.height = Math.round(props.size * deviceScale)
  context = canvas.value.getContext('2d')
  if (!context) return

  resolveColors()
  reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  reduceMotion.addEventListener('change', handleMotionPreferenceChange)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  startAnimation()
})

onBeforeUnmount(() => {
  stopAnimation()
  reduceMotion?.removeEventListener('change', handleMotionPreferenceChange)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <canvas
    ref="canvas"
    class="tq-solving-orb"
    :width="size"
    :height="size"
    :style="{ width: `${size}px`, height: `${size}px` }"
    aria-hidden="true"
  />
</template>

<style scoped>
.tq-solving-orb {
  display: block;
  max-width: 100%;
}
</style>
